import { createFileRoute, Navigate, redirect } from '@tanstack/react-router';

import { useAbility } from '@features/authorization';
import {
  normalizeMembershipRegisterSearch,
  type CrmMembershipRegisterFilters,
} from '@features/crm-customer-membership';
import { CrmMembershipsRegisterPage, CrmPrimaryNavigation } from '@pages/crm';
import type { RouterContext } from '@shared/config/router-context';
import {
  canAccessHelpdesk,
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
  canAccessCrmCustomerProfile,
} from '@shared/lib';

export const Route = createFileRoute('/$locale/_authenticated/crm/memberships/')({
  validateSearch: normalizeMembershipRegisterSearch,
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessCrmCustomerMembershipDirectory(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: CrmMembershipsRegisterRoute,
});

function CrmMembershipsRegisterRoute() {
  const { locale } = Route.useParams();
  const ability = useAbility();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const canAccessMemberships = canAccessCrmCustomerMembershipDirectory(ability);
  const canAccessProfiles = canAccessCrmCustomerDirectory(ability);

  if (!canAccessMemberships) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  const filters: CrmMembershipRegisterFilters = {
    dateFrom: search.membershipDateFrom,
    dateTo: search.membershipDateTo,
    inductingInstructorId: search.membershipInstructorId,
    membershipStatus: search.membershipStatus,
    membershipTypeId: search.membershipTypeId,
    ownerProfileId: search.membershipOwnerId,
  };

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmPrimaryNavigation
        canAccessHelpdesk={canAccessHelpdesk(ability)}
        activeSection="memberships"
        canAccessMemberships={canAccessMemberships}
        canAccessProfiles={canAccessProfiles}
        locale={locale}
      />
      <CrmMembershipsRegisterPage
        filters={filters}
        page={search.membershipPage ?? 1}
        q={search.membershipQ}
        sort={search.membershipSort}
        onFiltersChange={(nextFilters) => {
          void navigate({
            replace: true,
            resetScroll: false,
            search: {
              membershipDateFrom: nextFilters.dateFrom,
              membershipDateTo: nextFilters.dateTo,
              membershipInstructorId: nextFilters.inductingInstructorId,
              membershipOwnerId: nextFilters.ownerProfileId,
              membershipStatus: nextFilters.membershipStatus,
              membershipTypeId: nextFilters.membershipTypeId,
              membershipQ: search.membershipQ,
              membershipSort: search.membershipSort,
            },
          });
        }}
        onOpenMembership={(membershipId) => {
          void navigate({
            to: '/$locale/crm/memberships/$membershipId',
            params: { locale, membershipId },
            search,
          });
        }}
        onEditMembership={(membershipId) => {
          void navigate({
            to: '/$locale/crm/memberships/$membershipId',
            params: { locale, membershipId },
            search: { ...search, edit: '1' },
          });
        }}
        onOpenOwner={
          canAccessCrmCustomerProfile(ability)
            ? (customerId) => {
                void navigate({
                  to: '/$locale/crm/user-profiles/$userProfileId',
                  params: { userProfileId: customerId, locale },
                });
              }
            : undefined
        }
        onOpenInductingInstructor={
          canAccessCrmCustomerProfile(ability)
            ? (customerId) => {
                void navigate({
                  to: '/$locale/crm/user-profiles/$userProfileId',
                  params: { userProfileId: customerId, locale },
                });
              }
            : undefined
        }
        onPageChange={(membershipPage) => {
          void navigate({
            replace: true,
            resetScroll: false,
            search: {
              ...search,
              membershipPage: membershipPage === 1 ? undefined : membershipPage,
            },
          });
        }}
        onSearchChange={(membershipQ) => {
          void navigate({
            replace: true,
            resetScroll: false,
            search: {
              ...search,
              membershipPage: undefined,
              membershipQ,
            },
          });
        }}
        onSortChange={(membershipSort) => {
          void navigate({
            replace: true,
            resetScroll: false,
            search: {
              ...search,
              membershipPage: undefined,
              membershipSort,
            },
          });
        }}
      />
    </main>
  );
}
